import { useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import {
	getSiteTypes,
	getTechnologies,
	getUnlinkedCrowdCells,
	getUnlinkedSites,
	getVendors,
} from '@api/manageSite';

import { extractQueryParams } from '@utils/common';
import { optionsFormatted, optionsFormattedWithId } from '@utils/formatter';

import { EUserRoles, EVendorsValues } from '@enums/common';

import { refetchOptions } from '@constants/common';
import { SITE_TYPES, TECHNOLOGIES, UNLINKED_CROWDCELLS, UNLINKED_SITES, VENDORS } from '@constants/queryKeys';

export const useManageSitesOptions = (isSitesMode?: boolean) => {
	const {
		accessPermission: {
			networkOperatorTelecomSitesAttributeUpdater,
			networkOperatorTelecomSitesViewer,
		},
	} = useAppSelector(auth);

	const isUnlinkCrowdCellAvailable = (
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorTelecomSitesViewer
	);

	const isUnlinkedSitesAvailable = (
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorTelecomSitesViewer
	);

	const { data: vendors } = useQuery({
		queryKey: [VENDORS],
		queryFn: extractQueryParams(getVendors),
		allowedRoles: [
			EUserRoles.networkOperatorTelecomSitesViewer,
			EUserRoles.networkOperatorTelecomSitesAttributeUpdater,
			EUserRoles.networkOperatorKpiViewer,
			EUserRoles.networkOperatorKpiUpdater,
			EUserRoles.networkOperatorOrganizationAdministrator,
		],
		...typeof isSitesMode === 'boolean' && { enabled: [isSitesMode] },
		...refetchOptions,
	});

	const { data: siteTypes } = useQuery({
		queryKey: [SITE_TYPES],
		queryFn: extractQueryParams(getSiteTypes),
		...typeof isSitesMode === 'boolean' && { enabled: [isSitesMode] },
		refetchOnWindowFocus: false,
	});

	const { data: technologies, isLoading: isLoadingTechnologies } = useQuery({
		queryKey: [TECHNOLOGIES],
		queryFn: extractQueryParams(getTechnologies),
		...typeof isSitesMode === 'boolean' && { enabled: [isSitesMode] },
		refetchOnWindowFocus: false,
	});

	const {
		data: unlinkedSites,
		isLoading: isLoadingUnlinkedSites,
		isFetching: isFetchingUnlinkedSites,
	} = useQuery({
		queryKey: [UNLINKED_SITES],
		queryFn: extractQueryParams((params) => getUnlinkedSites({ vendor: EVendorsValues.limenet, ...params as any })),
		refetchOnWindowFocus: false,
		enabled: [isUnlinkedSitesAvailable],
	});

	const {
		data: unlinkedCrowdCells,
		isLoading: isLoadingUnlinkedCrowdCells,
		isFetching: isFetchingUnlinkedCrowdCells,
	} = useQuery({
		queryKey: [UNLINKED_CROWDCELLS],
		queryFn: extractQueryParams(getUnlinkedCrowdCells),
		refetchOnWindowFocus: false,
		enabled: [isUnlinkCrowdCellAvailable],
	});

	return {
		isLoadingUnlinkCrowdCellsForm: isLoadingUnlinkedCrowdCells || isFetchingUnlinkedCrowdCells,
		isLoadingUnlinkForm: isLoadingUnlinkedSites || isFetchingUnlinkedSites,
		isLoadingTechnologies,
		siteTypes,
		siteTypesOptions: optionsFormattedWithId(siteTypes || []),
		technologiesOptions: optionsFormatted(technologies || []),
		technologies,
		unlinkedCrowdCells: unlinkedCrowdCells || [],
		unlinkedSites: unlinkedSites || [],
		vendors: vendors || [],
		vendorsOptions: optionsFormatted(vendors || []),
	};
};
